import React, {useEffect, useMemo, useState } from "react";

const fieldBaseClass =
  "w-full rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-[var(--accent,#a51c1c)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25";

const activeFieldClass = `${fieldBaseClass} bg-white text-gray-800 placeholder:text-gray-400`;
const inactiveFieldClass = `${fieldBaseClass} bg-gray-50 text-gray-400 placeholder:text-gray-300`;

const UNASSIGNED_GL_CODE = "0000";

// Small, self-contained confirm-delete modal styled to match the app's
// existing "Confirm Delete" dialog (white card, bold header, gray warning
// box, Cancel + red destructive action).
const ConfirmDeleteModal = ({ label, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="confirm-delete-title"
  >
    <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
      <h3 id="confirm-delete-title" className="text-base font-semibold text-gray-900">
        Confirm Delete
      </h3>

      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
        This will remove <span className="font-semibold">{label}</span> from your GL code
        list.
      </div>

      <p className="mt-3 text-xs text-gray-500">
        This action cannot be undone.
      </p>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const BudgetGlCodesSettings = ({ codes, onCodesChange, loading }) => {
  const [search, setSearch] = useState("");
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  const [newCodeId, setNewCodeId] = useState(null);

  useEffect(() => {
    if (!newCodeId) return;
  
    const newRow = document.querySelector(
      `[data-code-id="${newCodeId}"]`
    );
  
    if (newRow) {
      newRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  
    setNewCodeId(null);
  }, [codes, newCodeId]);

  const addCode = () => {
    const newCode = {
      client_id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      gl_code: "",
      description: "",
      is_active: true,
    };
  
    onCodesChange([...codes, newCode]);
    setNewCodeId(newCode.client_id);
  };

  const updateCode = (index, patch) => {
    onCodesChange(
      codes.map((code, codeIndex) =>
        codeIndex === index ? { ...code, ...patch } : code,
      ),
    );
  };

  const requestRemoveCode = (index) => {
    setPendingRemoveIndex(index);
  };

  const cancelRemoveCode = () => {
    setPendingRemoveIndex(null);
  };

  const confirmRemoveCode = () => {
    if (pendingRemoveIndex === null) return;
    onCodesChange(codes.filter((_, codeIndex) => codeIndex !== pendingRemoveIndex));
    setPendingRemoveIndex(null);
  };

  const pendingCode = pendingRemoveIndex !== null ? codes[pendingRemoveIndex] : null;
  const pendingLabel = pendingCode
    ? [pendingCode.gl_code, pendingCode.description].filter(Boolean).join(" — ") ||
      "this GL code"
    : "";

  const filteredIndexes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return codes.map((_, index) => index);
    return codes
      .map((code, index) => ({ code, index }))
      .filter(({ code }) => {
        const gl = String(code?.gl_code ?? "").toLowerCase();
        const description = String(code?.description ?? "").toLowerCase();
        return gl.includes(query) || description.includes(query);
      })
      .map(({ index }) => index);
  }, [codes, search]);

  const activeCount = codes.filter((code) => code.is_active !== false).length;

  return (
    <div className="space-y-5 pb-2">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Budget GL Codes</h3>
        <p className="mt-1 max-w-3xl text-sm text-gray-500">
          The GL accounts users can code budgets and invoices against. Shared by
          every user of this tenant. Deactivate a code to hide it from new entries
          while keeping it on past budgets and invoices.
        </p>
      </div>

<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
  <p className="text-sm text-gray-600">
    <span className="font-semibold text-gray-900">{codes.length}</span>{" "}
    codes · {activeCount} active
  </p>

  <div className="flex items-center gap-2 whitespace-nowrap">
    <input
      type="text"
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      placeholder="Search code or name"
      disabled={loading}
      className={`${activeFieldClass} w-64`}
    />

    <button
      type="button"
      onClick={addCode}
      disabled={loading}
      className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      + Add GL code
    </button>
  </div>
</div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-[640px] w-full text-left">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2.5 font-medium">GL code</th>
              <th className="px-3 py-2.5 font-medium">Description</th>
              <th className="px-3 py-2.5 font-medium">Status</th>
              <th className="px-3 py-2.5 font-medium" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {filteredIndexes.map((index) => {
              const code = codes[index];
              const isActive = code.is_active !== false;
              const isUnassigned =
                String(code.gl_code || "").trim() === UNASSIGNED_GL_CODE;
              const rowFieldClass = isActive ? activeFieldClass : inactiveFieldClass;
              return (
                <tr key={code.id || code.client_id || index}
                data-code-id={code.client_id}
                >
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={code.gl_code || ""}
                      onChange={(event) =>
                        updateCode(index, { gl_code: event.target.value })
                      }
                      disabled={loading || isUnassigned}
                      className={rowFieldClass}
                      maxLength={100}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={code.description || ""}
                      onChange={(event) =>
                        updateCode(index, { description: event.target.value })
                      }
                      disabled={loading}
                      placeholder="Optional"
                      className={rowFieldClass}
                      maxLength={255}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                        onClick={() => updateCode(index, { is_active: !isActive })}
                        disabled={loading}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-50 ${
                          isActive ? "bg-emerald-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            isActive ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <span className={isActive ? "text-gray-700" : "text-gray-400"}>
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isUnassigned ? (
                      <span className="text-xs text-gray-400">Required</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => requestRemoveCode(index)}
                        disabled={loading}
                        className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {codes.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                  No GL codes configured.
                </td>
              </tr>
            )}
            {codes.length > 0 && filteredIndexes.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                  No GL codes match this search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pendingRemoveIndex !== null && (
        <ConfirmDeleteModal
          label={pendingLabel}
          onCancel={cancelRemoveCode}
          onConfirm={confirmRemoveCode}
        />
      )}
    </div>
  );
};

export default BudgetGlCodesSettings;
